/**
 * Batch embedding primitive and its failure taxonomy.
 *
 * embedTextBatch is the single entry point both the page and chunk passes use.
 * It sub-batches a flat text list, calls the provider's native embedBatch when
 * available, validates every response (cardinality, index order, per-vector
 * integrity), and degrades to sequential embed() for the cases where that is
 * safe (no embedBatch, request-too-large, retried-transient). Integrity and
 * auth failures throw — never silently fall back.
 *
 * Imports only a TYPE from provider.ts (erased at compile) and never a value,
 * so it introduces no runtime dependency on provider.ts. The provider name for
 * batch sizing is passed in explicitly by the orchestrator.
 */

import type { EmbeddingInputType, LLMProvider } from "./provider.js";
import {
  EMBED_BATCH_SIZES,
  EMBED_BATCH_SIZE_FALLBACK,
  EMBED_BATCH_CAPS,
  EMBED_BATCH_CAP_FALLBACK,
  ENV_EMBED_BATCH_SIZE,
  ENV_EMBED_STRICT,
} from "./constants.js";
import * as output from "./output.js";
import { EmbeddingIntegrityError, assertVectorValid, assertEveryVectorValid } from "./embeddings-validate.js";

// Re-export so existing test imports `{ EmbeddingIntegrityError } from embeddings-batch.js` keep working.
export { EmbeddingIntegrityError } from "./embeddings-validate.js";

/**
 * Wrap a provider so every embed / embedBatch call is counted. Lets a pass
 * report the exact number of provider requests it made — native batch calls
 * plus any sequential-fallback singles — without changing embedTextBatch's
 * signature.
 */
export function makeCountingProvider(
  provider: LLMProvider,
): { provider: LLMProvider; requestCount: () => number } {
  let requests = 0;
  const counting: LLMProvider = Object.create(provider);
  counting.embed = (text: string, inputType?: EmbeddingInputType) => {
    requests += 1;
    return provider.embed(text, inputType);
  };
  if (provider.embedBatch) {
    counting.embedBatch = (texts: string[], inputType?: EmbeddingInputType) => {
      requests += 1;
      return provider.embedBatch!(texts, inputType);
    };
  }
  return { provider: counting, requestCount: () => requests };
}

/** Read a numeric HTTP status off an error if present. */
function statusOf(err: unknown): number | undefined {
  const s = (err as { status?: unknown })?.status;
  return typeof s === "number" ? s : undefined;
}

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function isIntegrityError(err: unknown): boolean {
  return err instanceof EmbeddingIntegrityError;
}

export function isAuthError(err: unknown): boolean {
  const s = statusOf(err);
  if (s === 401 || s === 403) return true;
  return /api[_ ]?key|unauthor|forbidden|not set/i.test(messageOf(err));
}

export function isRequestTooLarge(err: unknown): boolean {
  const s = statusOf(err);
  if (s === 413) return true;
  if (s === 400 && /too large|maximum context|max .*token|token.*limit|payload|size/i.test(messageOf(err))) {
    return true;
  }
  return false;
}

export function isTransient(err: unknown): boolean {
  const s = statusOf(err);
  if (s === 429 || (s !== undefined && s >= 500)) return true;
  return /etimedout|econnreset|socket hang up|network|fetch failed|timeout/i.test(messageOf(err));
}

/**
 * Resolve the embedding batch size for the active provider. Per-provider default,
 * overridable by LLMWIKI_EMBED_BATCH_SIZE (positive integer), clamped to the
 * provider's documented input cap. Invalid overrides warn and fall back.
 */
export function resolveEmbedBatchSize(providerName: string): number {
  const def = EMBED_BATCH_SIZES[providerName] ?? EMBED_BATCH_SIZE_FALLBACK;
  const cap = EMBED_BATCH_CAPS[providerName] ?? EMBED_BATCH_CAP_FALLBACK;
  const raw = process.env[ENV_EMBED_BATCH_SIZE]?.trim();
  if (!raw) return def;

  const n = Number(raw);
  if (!Number.isInteger(n) || n <= 0) {
    output.status("!", output.warn(`${ENV_EMBED_BATCH_SIZE}="${raw}" is not a positive integer; using ${def}.`));
    return def;
  }
  if (n > cap) {
    output.status("!", output.warn(`${ENV_EMBED_BATCH_SIZE}=${n} exceeds the ${providerName} cap; clamping to ${cap}.`));
    return cap;
  }
  return n;
}

/** Split an array into fixed-size chunks. */
function chunked<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/** Native batch call with full response validation. Throws EmbeddingIntegrityError. */
async function validatedBatch(
  provider: LLMProvider,
  sub: string[],
  expectedDim?: number,
  inputType: EmbeddingInputType = "document",
): Promise<number[][]> {
  const vecs = await provider.embedBatch!(sub, inputType); // already index-normalized by the provider
  if (vecs.length !== sub.length) {
    throw new EmbeddingIntegrityError(`cardinality: got ${vecs.length} for ${sub.length} inputs`);
  }
  assertEveryVectorValid(vecs, expectedDim);
  return vecs;
}

/** Validated single-item fallback path. */
async function sequentialEmbed(
  provider: LLMProvider,
  sub: string[],
  expectedDim?: number,
  inputType: EmbeddingInputType = "document",
): Promise<number[][]> {
  const out: number[][] = [];
  for (const text of sub) {
    out.push(await embedOneSequential(provider, text, expectedDim, inputType));
  }
  return out;
}

/** Embed one item, retrying one transient single-item failure. */
async function embedOneSequential(
  provider: LLMProvider,
  text: string,
  expectedDim?: number,
  inputType: EmbeddingInputType = "document",
): Promise<number[]> {
  try {
    const v = await provider.embed(text, inputType);
    assertVectorValid(v, expectedDim);
    return v;
  } catch (err) {
    if (!isTransient(err)) throw err;
    const retried = await provider.embed(text, inputType);
    assertVectorValid(retried, expectedDim);
    return retried;
  }
}

/**
 * One retry of the native batch call; falls back to sequential on another
 * transient or oversized error, re-throws on anything else (integrity, auth, unknown).
 */
async function retryThenFallback(
  provider: LLMProvider,
  sub: string[],
  expectedDim?: number,
  inputType: EmbeddingInputType = "document",
): Promise<number[][]> {
  try {
    return await validatedBatch(provider, sub, expectedDim, inputType);
  } catch (retryErr) {
    if (isTransient(retryErr) || isRequestTooLarge(retryErr)) {
      return sequentialEmbed(provider, sub, expectedDim, inputType);
    }
    throw retryErr; // integrity / auth / unknown — surface it
  }
}

/**
 * Embed one sub-batch with the fallback policy:
 *   - no embedBatch         → sequential
 *   - integrity / auth      → throw (never fall back)
 *   - request-too-large     → sequential immediately (no retry)
 *   - transient             → ONE retry; if the retry fails with another
 *                             transient/oversized error → sequential; any other
 *                             retry error (integrity, auth, unknown) → throw
 *   - unknown               → throw
 */
async function embedSubBatch(
  provider: LLMProvider,
  sub: string[],
  expectedDim?: number,
  inputType: EmbeddingInputType = "document",
): Promise<number[][]> {
  if (!provider.embedBatch) return sequentialEmbed(provider, sub, expectedDim, inputType);
  try {
    return await validatedBatch(provider, sub, expectedDim, inputType);
  } catch (err) {
    if (isIntegrityError(err) || isAuthError(err)) throw err;
    if (isRequestTooLarge(err)) return sequentialEmbed(provider, sub, expectedDim, inputType);
    if (isTransient(err)) return retryThenFallback(provider, sub, expectedDim, inputType);
    throw err; // unknown — surface it
  }
}

/**
 * Embed `texts` in sequential sub-batches via embedSubBatch. On a terminal
 * failure, annotate the thrown error with `failedIndex` (the global index of the
 * first item in the failing sub-batch) so the page/chunk pass can name the
 * offending slug in its report. The error type is preserved (no wrapper), so
 * the taxonomy classifiers still apply to it directly.
 */
export async function embedTextBatch(
  provider: LLMProvider,
  texts: string[],
  batchSize: number,
  expectedDim?: number,
  inputType: EmbeddingInputType = "document",
): Promise<number[][]> {
  if (texts.length === 0) return [];
  const out: number[][] = [];
  for (const sub of chunked(texts, batchSize)) {
    const startIndex = out.length;
    try {
      out.push(...(await embedSubBatch(provider, sub, expectedDim, inputType)));
    } catch (err) {
      if (err && typeof err === "object" && (err as { failedIndex?: number }).failedIndex === undefined) {
        (err as { failedIndex?: number }).failedIndex = startIndex;
      }
      throw err;
    }
  }
  return out;
}

/**
 * Embed a work-list via {@link embedTextBatch}, mapping each item to its text and
 * enriching any terminal failure with the offending item's label (pass + slug/
 * pageId at the failing index). Shared by the page and chunk passes so the
 * batch-call + error-enrichment idiom lives in one place.
 *
 * @param provider - The (counting) embedding provider.
 * @param items - The work items to embed, in order.
 * @param getText - Map an item to the text sent to the provider.
 * @param getLabel - Map a work index to the label named in the error.
 * @param pass - `"page"` or `"chunk"` — names the failing pass in the error.
 * @returns One vector per item, in input order.
 */
export async function embedWorkItems<T>(
  provider: LLMProvider,
  items: T[],
  getText: (item: T) => string,
  getLabel: (index: number) => string | undefined,
  pass: "page" | "chunk",
  batchSize: number,
  expectedDim?: number,
): Promise<number[][]> {
  try {
    return await embedTextBatch(provider, items.map(getText), batchSize, expectedDim);
  } catch (err) {
    throw enrichEmbedError(err, pass, getLabel);
  }
}

export type EmbeddingErrorClass = "integrity" | "auth" | "request-too-large" | "transient" | "unknown";

/** Map any embedding error onto a stable, user-facing class label. */
export function classifyEmbeddingError(err: unknown): EmbeddingErrorClass {
  if (isIntegrityError(err)) return "integrity";
  if (isAuthError(err)) return "auth";
  if (isRequestTooLarge(err)) return "request-too-large";
  if (isTransient(err)) return "transient";
  return "unknown";
}

/**
 * Wrap a failed-batch error into a reportable one naming the pass, the failure
 * class, and the slug at the failing index (from embedTextBatch's `failedIndex`
 * annotation). Preserves the original error as `cause` so downstream code can
 * still classify it.
 */
export function enrichEmbedError(
  err: unknown,
  pass: "page" | "chunk",
  slugAt: (index: number) => string | undefined,
): Error {
  const index = (err as { failedIndex?: number })?.failedIndex ?? 0;
  const slug = slugAt(index) ?? "?";
  const cls = classifyEmbeddingError(err);
  const detail = err instanceof Error ? err.message : String(err);
  return Object.assign(new Error(`${pass} embedding failed [${cls}] at "${slug}": ${detail}`), { cause: err });
}

/** True when LLMWIKI_EMBED_STRICT is set — any embedding failure should exit non-zero. */
export function shouldRethrowEmbeddingFailure(): boolean {
  const value = process.env[ENV_EMBED_STRICT]?.trim().toLowerCase();
  return value === "1" || value === "true" || value === "yes" || value === "on";
}

/**
 * Shared catch handler for the safelyUpdateEmbeddings wrappers. Logs the
 * warning and re-throws when strict mode is active.
 */
export function handleSafeEmbeddingFailure(err: unknown, warningLine: string): void {
  output.status("!", output.warn(warningLine));
  if (shouldRethrowEmbeddingFailure()) throw err;
}
